import { apiFetch } from '@/shared/api/client'
import { isMockApi } from '@/shared/env'
import type { KitchenDeviceBinding } from '@/shared/auth/deviceStorage'

export type ActivationPreview = {
  device_name: string
  kitchen_name: string
  device_type: 'kitchen_tablet' | 'pos_tablet' | 'delman_mobile'
}

export type ActivateDeviceRequest = {
  activation_code: string
  device_uid: string
  os?: string
}

export type ActivateDeviceResponse = {
  device_id: string
  device_uid: string
  device_name: string
  device_type: 'kitchen_tablet'
  kitchen_id: string
  kitchen_code: string
  kitchen_name: string
}

const MOCK_CODE = 'X7K9M4'

const MOCK_PREVIEW: ActivationPreview = {
  device_name: 'Tablet Produksi 1',
  kitchen_name: 'Dapur A',
  device_type: 'kitchen_tablet',
}

function useMockApi(): boolean {
  return isMockApi
}

function mockPreview(code: string): ActivationPreview {
  if (!code.trim()) {
    throw new Error('Kode tidak valid atau sudah kedaluwarsa.')
  }
  // Dev fallback: keep mock deterministic but don't lock to one code.
  if (code === MOCK_CODE) {
    return MOCK_PREVIEW
  }
  const suffix = code.replace(/[^A-Z0-9]/g, '').slice(-4) || '0001'
  return {
    device_name: `Tablet Produksi ${suffix}`,
    kitchen_name: 'Dapur A',
    device_type: 'kitchen_tablet',
  }
}

function mockActivate(req: ActivateDeviceRequest): ActivateDeviceResponse {
  const preview = mockPreview(req.activation_code)
  return {
    device_id: `dev-kitchen-${req.device_uid.slice(0, 8)}`,
    device_uid: req.device_uid,
    device_name: preview.device_name,
    device_type: 'kitchen_tablet',
    kitchen_id: '01903200-0000-7000-8000-000000000011',
    kitchen_code: 'DAPUR-A',
    kitchen_name: preview.kitchen_name,
  }
}

export function toKitchenBinding(res: ActivateDeviceResponse): KitchenDeviceBinding {
  return {
    deviceId: res.device_id,
    deviceUid: res.device_uid,
    deviceName: res.device_name,
    kitchenId: res.kitchen_id,
    kitchenCode: res.kitchen_code,
    kitchenName: res.kitchen_name,
    deviceType: res.device_type,
    activatedAt: new Date().toISOString(),
  }
}

export async function lookupActivationCode(code: string): Promise<ActivationPreview> {
  if (useMockApi()) {
    await new Promise((r) => setTimeout(r, 350))
    return mockPreview(code)
  }

  const encoded = encodeURIComponent(code)
  return apiFetch<ActivationPreview>(`/auth/activation-codes/preview?code=${encoded}`)
}

export async function activateKitchenDevice(req: ActivateDeviceRequest): Promise<KitchenDeviceBinding> {
  if (useMockApi()) {
    await new Promise((r) => setTimeout(r, 1200))
    return toKitchenBinding(mockActivate(req))
  }

  const res = await apiFetch<ActivateDeviceResponse>('/auth/activate-device', {
    method: 'POST',
    body: JSON.stringify(req),
  })
  return toKitchenBinding(res)
}
